import { Component, EventEmitter, Input, Output } from "@angular/core";
import { formatMessage } from "devextreme/localization";
import { CalculatorUsFieldItemsService } from "../../../../../services/calculator-us-field-items.service";
import { UsCalculatorFieldItem } from "../../../../../model/calculator-us-remastered/usCalculatorFieldItem";
import { SummaryItem } from "../../../../../model/calculator-us-remastered/summaryItem";


/**
 * Represents a selected option with an associated identifier, value, and name.
 *
 * This class is typically used to encapsulate the details of a selected option,
 * where the option has a unique identifier, an optional value, and a display name.
 *
 * This class is the value of this component's `selectedOption` output.
 */
export class SelectedOption {
  optionId: string;
  optionValue: string | undefined;
  optionName: string;
}


/**
 * Represents an input field with a summary of essential metadata.
 *
 * This class is designed to store metadata about a specific input field,
 * including its identifier, display label, and categorization.
 *
 * This class is needed to generate the summary of the selected options.
 */
export class SummaryFieldInput {
  name: string;
  label: string;
  category: string;
}

/**
 * Represents a select box field item that extends the UsCalculatorFieldItem interface.
 * Includes an optional property for a prettified version of the display value.
 *
 * @interface SelectBoxFieldItem
 * @extends UsCalculatorFieldItem
 *
 * @property {string} [prettifiedDisplayValue] - An optional property for storing a user-friendly or formatted version of the display value.
 */
interface SelectBoxFieldItem extends UsCalculatorFieldItem {
  prettifiedDisplayValue?: string;
}

/**
 * Component used to render and manage a select box with optional text input fields,
 * supporting advanced features like dynamic filtering, dependency-based updates,
 * and custom event emissions for state changes.
 *
 * The resulting value represents a calculator option.
 *
 * The component's functionality is driven by various input and output properties,
 * enabling integration into complex UI environments. It allows conditional rendering
 * of options based on dependencies like classification, associations to publishers,
 * and selected values.
 */
@Component({
  selector: "app-option-mapping-select-box",
  templateUrl: "./option-mapping-select-box.component.html",
  styleUrls: ["./option-mapping-select-box.component.scss"],
})
export class OptionMappingSelectBoxComponent {
  /**
   * Represents the unique identifier of a publisher.
   * This variable is optional and can be undefined if the publisher ID is not assigned or available.
   * Used to associate data or operations with a specific publisher.
   */
  private _publisherId: string | undefined;
  @Input()
  get publisherId(): string | undefined {
    return this._publisherId;
  }
  set publisherId(val: string | undefined) {
    this._publisherId = val;
  }

  /**
   * Represents the classification or category assigned to an entity.
   * This variable can be a string indicating a specific classification,
   * or undefined if no classification is set.
   */
  private _classification: string | undefined;
  @Input()
  get classification(): string | undefined {
    return this._classification;
  }
  set classification(val: string | undefined) {
    this._classification = val;
    if (val !== undefined) {
      this.loadFieldItems();
    }
  }

  /**
   * Represents a text label that can be used to identify or describe an element or item.
   * The label can either hold a string value or be undefined if no label is assigned.
   *
   * If it is undefined, the label will be derived from the summaryField property.
   */
  @Input() label: string | undefined;

  /**
   * Represents a specific subgroup or division within a broader category.
   * This variable is typically used to classify or organize items into more specific groups
   * for better categorization and retrieval.
   * Refers to the subcategory field in the database for the UI field items.
   */
  @Input() subcategory: string;
  /**
   * Represents the name input provided by the user.
   * This variable is expected to hold a string value,
   * which can be used for identification or labeling purposes.
   * Refers to the inputName field in the database for the UI field items.
   */
  @Input() inputName: string;

  /**
   * Represents a field whose value is dependent on external conditions or another field.
   * This variable can either hold a string value or be undefined, indicating the absence of a value.
   * Refers to the dependsOnField field in the database for the UI field items.
   */
  private _dependsOnField: string | undefined;
  @Input()
  get dependsOnField(): string | undefined {
    return this._dependsOnField;
  }
  set dependsOnField(val: string | undefined) {
    this._dependsOnField = val;
    this.updateInternalFields();
  }

  /**
   * Stores a value that is conditionally set based on specific logic or dependencies.
   * The value can either be a string or remain undefined if not initialized or applicable.
   * This variable is typically used to represent a state or result that depends on
   * external factors, such as user actions, configuration settings, or calculation results.
   * Refers to the dependsOnValue field in the database for the UI field items.
   */
  private _dependsOnValue: string | undefined;
  @Input() get dependsOnValue(): string | undefined {
    return this._dependsOnValue;
  }
  set dependsOnValue(val: string | undefined) {
    this._dependsOnValue = val;
    this.updateInternalFields();
  }

  /**
   * Represents the input field for a summary section.
   *
   * This variable can either hold a `SummaryFieldInput` object or remain undefined
   * if no summary field input is provided. It is used to define or manage the content
   * of the summary field in a given context or system.
   *
   * Type:
   * - `SummaryFieldInput`: Represents the input structure for the summary.
   * - `undefined`: Indicates no summary field input is specified.
   *
   * If no summary field input is provided, the summaryUpdate event will not be emitted.
   */
  @Input() summaryField: SummaryFieldInput | undefined;

  /**
   * An EventEmitter instance used to emit and listen to updates
   * on a collection of summary items.
   *
   * This emitter notifies subscribers whenever there is a change
   * in the summary items. The emitted event passes an array of
   * `SummaryItem` objects containing the updated summary data.
   *
   * It is only emitted when the summaryField input is defined.
   *
   * @type {EventEmitter<SummaryItem[]>}
   */
  @Output() summaryUpdate = new EventEmitter<SummaryItem[]>();

  /**
   * EventEmitter that emits when the value of a selection changes.
   * It emits either the selected option or undefined if no option is selected.
   *
   * It is emitted when the selection of the select box changes (the referenced optionId)
   * or the value of the text box changes (the referenced optionValue).
   *
   * @type {EventEmitter<SelectedOption | undefined>}
   */
  @Output() onValueChanged = new EventEmitter<SelectedOption | undefined>();

  /**
   * EventEmitter that triggers whenever there is a change in the field items.
   * Emits an array of `UsCalculatorFieldItem` objects representing the updated field items.
   *
   * This event can be used to listen for changes and react accordingly
   * when the field items in the calculator are modified.
   *
   * This event is emitted when the possible `select`s of the select box change e.g., when the
   * dependsOnValue input changes. The parent component is usually not interested in this event.
   * In case the parent component needs full control over summary updates, then it should listen.
   */
  @Output() onFieldItemsChanged = new EventEmitter<UsCalculatorFieldItem[]>();

  formatMessage = formatMessage;

  /**
   * Represents the currently selected option field (the select box).
   * It can either contain an instance of `SelectBoxFieldItem` representing the selected option
   * or be `null` if no option is selected.
   *
   * The variable may change based on user interaction or programmatic updates to reflect
   * the current selection state within a select box field.
   */
  private _selectedOptionField: SelectBoxFieldItem | null = null;
  get selectedOptionField(): SelectBoxFieldItem | null {
    return this._selectedOptionField;
  }
  /**
   * Sets the selected option field and updates related values and state.
   * Clears and disables the value text box if the selected option has no associated value.
   * Emits value change and summary update events after the operation.
   *
   * @param {SelectBoxFieldItem | null} value - The selected option field item to set, or null to clear the selection.
   */
  set selectedOptionField(value: SelectBoxFieldItem | null) {
    this._selectedOptionField = value;
    // clear and disable the value text box when is selected option has no value
    if (
      value === null ||
      this.valueForSelects[value.keyValue!!] === undefined
    ) {
      this.selectedOptionValue = "";
      this.valueTextBoxDisabled = true;
    } else {
      this.selectedOptionValue =
        this.valueForSelects[value.keyValue!!]?.defaultValue || "";
      this.valueTextBoxDisabled = false;
    }
    this.emitValueChanged();
    this.emitUpdateSummary();
  }

  /**
   * Represents the value of the currently selected option.
   * This variable is typically used to store the value of a user-selected option
   * from a dropdown menu, radio button group, or similar interface.
   *
   * Default value is an empty string, indicating no option has been selected.
   */
  private _selectedOptionValue: string = "";
  get selectedOptionValue(): string {
    return this._selectedOptionValue;
  }
  set selectedOptionValue(value: string) {
    this._selectedOptionValue = value;
    this.emitUpdateSummary();
    this.emitValueChanged();
  }

  // all calculator field items for this select box
  /**
   * Represents a collection of items used in the US calculator field which
   * match the provided publisher ID, classification, and subcategory inputs.
   *
   * The `calculatorFieldItems` array is loaded when the classification input changes.
   * The subcategory input is not intended to be changed dynamically,
   */
  calculatorFieldItems: UsCalculatorFieldItem[] = [];
  /**
   * Represents a subselection of the calculatorFieldItems array that contains only the items
   * for the select box field which match with the dependsOn input values.
   *
   * This variable is used to populate the options of the select box element.
   *
   * @type {SelectBoxFieldItem[]}
   */
  selectsDataSource: SelectBoxFieldItem[] = [];
  /**
   * Represents a subselection of the calculatorFieldItems array that contains only the items
   * for the input test field for a specific selected option (via optionId).
   *
   * Key-Value Structure:
   * - Key (string): A unique identifier (optionId) for each select box item.
   * - Value (SelectBoxFieldItem): An object representing the properties and metadata
   *   associated with the selectable item.
   */
  valueForSelects: { [key: string]: SelectBoxFieldItem } = {};

  /**
   * Indicates whether the value textbox is necessary for the operation or functionality.
   * This boolean variable determines if the associated input field is required and rendered.
   */
  valueTextBoxNecessary = false;

  /**
   * Represents the disabled state of a text input box.
   * When set to `true`, the text input box is disabled and user interaction is restricted.
   * Typically used to prevent user modifications to the input under certain conditions.
   */
  valueTextBoxDisabled = true;

  constructor(
    private calculatorUsFieldItemsService: CalculatorUsFieldItemsService
  ) {}

  /**
   * Loads field items based on the publisher ID, classification, subcategory, and input name.
   * Ensures required values are defined before fetching the field items.
   * Fetches the field items from the data service and updates the internal fields with the retrieved data.
   *
   * @return {void} This method does not return any value.
   */
  private loadFieldItems(): void {
    if (
      !this.publisherId ||
      !this.classification ||
      !this.subcategory ||
      !this.inputName
    ) {
      return;
    }

    this.calculatorUsFieldItemsService
      .getFieldItemsBySubcategory(
        this.publisherId,
        this.calculatorUsFieldItemsService.getDbClassification(
          this.classification
        ),
        this.subcategory
      )
      .subscribe((fieldItems) => {
        this.calculatorFieldItems = [...fieldItems];
        this.updateInternalFields();
      });
  }

  /**
   * Updates the internal fields and data structures based on the `calculatorFieldItems` and their properties.
   * This method processes field items to populate `selectsDataSource`, `valueForSelects`, and `selectedOptionField`.
   * It also determines if a value text box is necessary and emits relevant updates.
   *
   * @return {void} This method does not return a value.
   */
  private updateInternalFields() {
    const loadedSelectsDataSource: SelectBoxFieldItem[] = [];
    const loadedValueForSelects: { [key: string]: SelectBoxFieldItem } = {};
    let loadedSelectedOptionField: SelectBoxFieldItem | null = null;

    for (const fieldItem of this.calculatorFieldItems) {
      if (fieldItem.inputName === `${this.inputName}`) {
        if (
          fieldItem.inputType === "OPTION_SELECT" &&
          this.matchesDependsOn(fieldItem)
        ) {
          const prettifiedFieldItem = <SelectBoxFieldItem>{
            ...fieldItem,
            prettifiedDisplayValue: this.prettifyDisplayValue(
              fieldItem.displayValue
            ),
          };
          loadedSelectsDataSource.push(prettifiedFieldItem);
          if (fieldItem.isDefault) {
            loadedSelectedOptionField = prettifiedFieldItem;
          }
        } else if (fieldItem.inputType === `OPTION_TEXT`) {
          loadedValueForSelects[fieldItem.dependsOnValue!!] = fieldItem;
        }
      }
    }

    this.selectsDataSource = [...loadedSelectsDataSource];
    this.valueForSelects = { ...loadedValueForSelects };
    this.selectedOptionField = loadedSelectedOptionField;
    this.valueTextBoxNecessary = Object.keys(loadedValueForSelects).length > 0;

    this.onFieldItemsChanged.emit(this.selectsDataSource);
    this.emitUpdateSummary();
  }

  /**
   * Checks if the given field item matches the dependency requirements based on `dependsOnField` and `dependsOnValue`.
   *
   * - if the field item does not have a dependsOnField, then return true
   * - if it has one and matches the one from this select box, then compare the dependsOnValues
   * - otherwise return false
   *
   * @param {UsCalculatorFieldItem} fieldItem - The field item to check for dependency matching.
   * @return {boolean} - Returns `true` if the field item matches the dependency conditions or does not have a
   * dependency. Returns `false` otherwise.
   */
  private matchesDependsOn(fieldItem: UsCalculatorFieldItem): boolean {
    if (!fieldItem.dependsOnField) {
      return true;
    } else if (this.dependsOnField === fieldItem.dependsOnField) {
      return this.dependsOnValue === fieldItem.dependsOnValue;
    }
    return false;
  }

  /**
   * Transforms the provided display value by replacing all pipe ('|') characters with hyphens ('-').
   * If the input is undefined, returns an empty string.
   */
  private prettifyDisplayValue(displayValue: string | undefined): string {
    return displayValue?.replace(/\|/g, "-") || "";
  }

  /**
   * Emits a value change event for the selected option.
   *
   * If no option is selected (`selectedOptionField` is undefined), it emits `undefined`.
   * Otherwise, it emits an object containing the selected option's ID, value, and name.
   */
  private emitValueChanged() {
    if (!this.selectedOptionField) {
      this.onValueChanged.emit(undefined);
    } else {
      const value = <SelectedOption>{
        optionId: this.selectedOptionField.keyValue,
        optionValue:
          this.selectedOptionValue === ""
            ? undefined
            : this.selectedOptionValue,
        optionName: this.selectedOptionField.displayValue,
      };
      this.onValueChanged.emit(value);
    }
  }

  /**
   * Emits an update to the summary with relevant summary items based on the
   * current state of the selected options and fields.
   *
   * Aggregates and prepares an array of `SummaryItem` objects representing the selected
   * options or values and their corresponding default values, then emits the summary
   * update event with this array.
   *
   * @return {void} This method does not return a value, but triggers an update
   * to the summary via the `summaryUpdate` event.
   */
  private emitUpdateSummary() {
    let summaryItemsToEmit: SummaryItem[] = [];

    if (!this.summaryField) {
      return;
    }

    // find the selected option and add it to the summary
    if (this.selectedOptionField !== null) {
      const defaultItem =
        this.selectsDataSource.find((item) => item.isDefault) || null;
      const summaryItemOptionSelect = new SummaryItem(
        this.summaryField.name,
        this.summaryField.label,
        this.selectedOptionField.displayValue || "",
        defaultItem?.displayValue || "",
        this.summaryField.category,
        undefined
      );
      summaryItemsToEmit.push(summaryItemOptionSelect);

      if (this.selectedOptionValue !== "") {
        const defaultOptionValue =
          this.valueForSelects[this.selectedOptionField.keyValue || ""];
        const summaryItemOptionValue = new SummaryItem(
          `${this.summaryField.name}Value`,
          this.summaryField.label,
          this.selectedOptionValue,
          defaultOptionValue?.defaultValue || "",
          this.summaryField.category,
          undefined
        );
        summaryItemsToEmit.push(summaryItemOptionValue);
      }
    }

    this.summaryUpdate.emit(summaryItemsToEmit);
  }
}
